// PWA registration with iframe + preview-host guards.
// Service worker is intentionally NOT registered inside Lovable's editor
// preview because it causes stale-cache and navigation issues.

const isInIframe = (() => {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
})();

const isPreviewHost =
  typeof window !== "undefined" &&
  (window.location.hostname.includes("id-preview--") ||
    window.location.hostname.includes("lovableproject.com"));

export const PWA_DISABLED = isInIframe || isPreviewHost;

export async function registerPWA() {
  if (typeof window === "undefined") return;

  if (PWA_DISABLED) {
    // Defensive cleanup: if a previous build registered a SW on this origin,
    // unregister it AND wipe its caches so we never serve stale chunks here.
    if ("serviceWorker" in navigator) {
      try {
        const regs = await navigator.serviceWorker.getRegistrations();
        const hadController = !!navigator.serviceWorker.controller;
        await Promise.all(regs.map((r) => r.unregister()));
        if (typeof caches !== "undefined") {
          const keys = await caches.keys();
          await Promise.all(keys.map((k) => caches.delete(k)));
        }
        // If the page was controlled by the now-removed SW, the currently
        // running JS is likely stale. Force one hard reload so the next load
        // fetches fresh assets directly from the network. Guarded by a
        // sessionStorage flag to avoid reload loops.
        if (hadController && regs.length > 0) {
          const RELOAD_FLAG = "ms:sw-cleanup-reload";
          if (!sessionStorage.getItem(RELOAD_FLAG)) {
            sessionStorage.setItem(RELOAD_FLAG, "1");
            window.location.reload();
          }
        }
      } catch {
        // best-effort; never block the app
      }
    }
    return;
  }

  if (!("serviceWorker" in navigator)) return;

  try {
    const { registerSW } = await import("virtual:pwa-register");
    registerSW({
      immediate: true,
      onNeedRefresh() {
        window.location.reload();
      },
      onRegisteredSW(_swUrl, registration) {
        if (!registration) return;
        registration.update().catch(() => undefined);

        if (registration.waiting) {
          registration.waiting.postMessage({ type: "SKIP_WAITING" });
          window.location.reload();
          return;
        }

        registration.addEventListener("updatefound", () => {
          const installing = registration.installing;
          if (!installing) return;
          installing.addEventListener("statechange", () => {
            if (installing.state === "installed" && navigator.serviceWorker.controller) {
              window.location.reload();
            }
          });
        });
      },
      onRegisterError(error) {
        console.warn("[PWA] registration failed", error);
      },
    });
  } catch (err) {
    console.warn("[PWA] registration failed", err);
  }
}

// VAPID public key — safe to expose. Used to subscribe browser to push.
export const VAPID_PUBLIC_KEY =
  "BDKfH6JEKb3FoVsZ_RaijFsyTAMkXBaUk7Xxo8uxqz28U_otzYpNcp32kDSL5Ha4HdccfA3MOq2aeuL5YFG8NLA";

export function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  const output = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) output[i] = rawData.charCodeAt(i);
  return output;
}

export function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    // iOS Safari
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

export type Platform = "ios" | "android" | "desktop" | "other";

export function detectPlatform(): Platform {
  if (typeof navigator === "undefined") return "other";
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua) && !(window as Window & { MSStream?: unknown }).MSStream)
    return "ios";
  if (/Android/i.test(ua)) return "android";
  if (/Win|Mac|Linux/i.test(ua)) return "desktop";
  return "other";
}
